/**
 * NotificationRow — one row of the Notifications feed (WSRPDM-0015, contract
 * §4.1).
 *
 * Localizes off `type` + `params`, never `message` — `t('notifType.'+type+
 * '.title', notificationTypeLabel(type))` / `.body` (registry.ts,
 * WSRAPI-0035). An unmapped `type` has no `notifType.<type>.*` catalog
 * entry at all, so both lookups fall through to their fallback argument:
 * the title falls back to the raw enum (via `notificationTypeLabel`'s own
 * raw-enum fallback) and the body falls back to the pre-rendered English
 * `message` — `useCopy()`'s local `interpolate()` leaves `message` unchanged
 * since it has no `{{}}` placeholders of its own. `common.unmapped` marks
 * that fallback explicitly rather than leaving it silent.
 */
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { isUnmappedNotificationType, notificationTypeLabel } from '@/domain/registry';
import { SeverityPill } from '@/components/ui';
import type { NotificationStatus, PlatformNotificationView } from '@/api/wsr/types';

/**
 * `registry.ts`'s `TFn` is label-only (`(key, fallback?) => string`) —
 * too narrow for this row's `{{param}}`-interpolated templates.
 * `useCopy()`'s actual `t` (3-arg, values last) satisfies this wider shape
 * structurally (mirrors `routes/queue/jobTimeline.ts`'s `TranslateFn`).
 */
type TranslateFn = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

const STATUS_STYLE: Record<
  NotificationStatus,
  { color: 'warning' | 'default' | 'success'; variant: 'filled' | 'outlined' }
> = {
  OPEN: { color: 'warning', variant: 'filled' },
  ACKNOWLEDGED: { color: 'default', variant: 'outlined' },
  RESOLVED: { color: 'success', variant: 'filled' },
};

/** Read a string param, falling back when absent/non-string — `params` is `Record<string, unknown>`. */
function paramString(params: Record<string, unknown>, key: string, fallback = ''): string {
  const value = params[key];
  return typeof value === 'string' ? value : fallback;
}

function notificationTitle(n: PlatformNotificationView, t: TranslateFn): string {
  return t(`notifType.${n.type}.title`, notificationTypeLabel(n.type, t));
}

function notificationBody(n: PlatformNotificationView, t: TranslateFn): string {
  const values: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(n.params)) {
    if (typeof value === 'string' || typeof value === 'number') values[key] = value;
  }
  const body = t(`notifType.${n.type}.body`, n.message, values);
  // A param the server did not send (e.g. an older row) leaves a literal `{{name}}` in the
  // localized text; never render that — the server's own message is the safe fallback.
  return body.includes('{{') ? n.message : body;
}

interface NotificationRowProps {
  notification: PlatformNotificationView;
  onAcknowledge: (id: number) => void;
  busy: boolean;
}

export function NotificationRow({ notification, onAcknowledge, busy }: NotificationRowProps) {
  const { t } = useCopy();
  const unmapped = isUnmappedNotificationType(notification.type, t);
  const statusStyle = STATUS_STYLE[notification.status];
  const showOpenTenant = notification.tenantSlug !== '*';

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      aria-label={`${notificationTitle(notification, t)} — ${notification.tenantSlug}`}
    >
      <Stack spacing={1}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <SeverityPill severity={notification.severity} />
            <Typography variant="subtitle2" noWrap>
              {notificationTitle(notification, t)}
            </Typography>
            {unmapped ? (
              <Chip size="small" variant="outlined" label={t('common.unmapped', 'Unmapped')} />
            ) : null}
          </Box>
          <Chip
            label={t(`notifications.status.${notification.status}`, notification.status)}
            color={statusStyle.color}
            variant={statusStyle.variant}
            size="small"
          />
        </Box>

        <Typography variant="body2">{notificationBody(notification, t)}</Typography>

        {notification.type === 'CREDENTIAL_INVALID' ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
            }}
          >
            <Typography variant="caption">
              {t(
                'notifications.remedy.credentialInvalid',
                'Re-authenticate the {{integration}} credential in saas-auth, then re-enable {{jobType}}.',
                {
                  integration: paramString(notification.params, 'integration'),
                  jobType: paramString(
                    notification.params,
                    'jobType',
                    notification.entityRef ?? '',
                  ),
                },
              )}
            </Typography>
          </Box>
        ) : null}

        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          {notification.tenantSlug !== '*' ? (
            <Typography variant="caption" color="text.secondary">
              {notification.tenantSlug}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {t('notifications.meta.occurrences', '{{n}}× seen', {
              n: notification.occurrenceCount,
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('notifications.meta.lastSeen', 'Last seen {{time}}', {
              time: new Date(notification.lastSeenAt).toLocaleString(),
            })}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          {notification.status === 'OPEN' ? (
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => onAcknowledge(notification.id)}
            >
              {t('notifications.actions.acknowledge', 'Acknowledge')}
            </Button>
          ) : null}
          {showOpenTenant ? (
            <Button size="small" component={Link} to={`/tenants?tenant=${notification.tenantSlug}`}>
              {t('notifications.actions.openTenant', 'Open tenant')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
